// Assembles the Cost Summary (8C) stacked series.
//
// `fn_get_aim_cost_summary` is per-run: it takes p_run_id and returns one row per
// provider, with no run_id or run_date on the rows. So a cost-over-runs chart
// needs one call per run and the run identity has to come from the caller.
//
// Verified 2026-07-29 on develop: historical runs do return the full per-provider
// split, and each run's provider costs sum exactly to its `total_cost_usd` from
// fn_get_aim_runs — $13.47, $4.09, $4.34, $3.84, $5.07 across the five runs in
// range. An earlier note on this card claimed the breakdown was unavailable for
// historical runs; that was wrong.

/**
 * Build the stacked chart rows, provider order and legend from per-run cost data.
 *
 * One chart row per run, keyed on run id rather than date so two runs on the same
 * day stay distinct. Each provider becomes a `dataKey` on that row.
 *
 * Providers are ordered by total spend across the whole window, descending, so
 * the largest contributor sits at the same position in every bar and the stack
 * order does not shuffle between runs.
 *
 * @param {Array<object>|null|undefined} runs runs in display order (oldest first)
 * @param {Map<string, Array<object>>|null|undefined} costByRunId per-run
 *   `fn_get_aim_cost_summary` rows, keyed on run id. A run missing from the map
 *   is reported in `missingRuns` rather than charted as zero — a zero-height
 *   segment reads as "free", not "not fetched".
 * @returns {{chartData: Array<object>, providers: Array<string>,
 *   dateByKey: Record<string, string>, missingRuns: number}}
 */
export function buildCostSeries(runs, costByRunId) {
  const list = Array.isArray(runs) ? runs : [];
  const byId = costByRunId instanceof Map ? costByRunId : new Map();

  const totalByProvider = new Map();
  const chartData = [];
  let missingRuns = 0;

  for (const run of list) {
    if (!run || !run.id) continue;
    const rows = byId.get(run.id);
    if (!Array.isArray(rows) || rows.length === 0) {
      missingRuns += 1;
      continue;
    }
    const point = { runKey: run.id, date: run.run_date };
    for (const r of rows) {
      const provider = r?.provider;
      const cost = Number(r?.cost_usd);
      if (!provider || !Number.isFinite(cost)) continue;
      // A provider can appear once per run; add rather than overwrite so a
      // duplicated row cannot silently drop spend.
      point[provider] = (point[provider] ?? 0) + cost;
      totalByProvider.set(provider, (totalByProvider.get(provider) ?? 0) + cost);
    }
    chartData.push(point);
  }

  const providers = [...totalByProvider.entries()]
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .map(([provider]) => provider);

  return {
    chartData,
    providers,
    dateByKey: Object.fromEntries(chartData.map((d) => [d.runKey, d.date])),
    missingRuns,
  };
}

/**
 * Runs to chart, newest-biased when the window is larger than the fetch budget.
 *
 * Each run costs one RPC call, so an all-time range would fan out to as many
 * calls as there are runs. Rather than truncate silently, callers get the count
 * that was dropped so the card can say so.
 *
 * @param {Array<object>} runsInRange runs already filtered to the date range
 * @param {number} maxRuns fetch budget
 * @returns {{runs: Array<object>, dropped: number}} `runs` oldest-first
 */
export function limitCostRuns(runsInRange, maxRuns) {
  const list = Array.isArray(runsInRange) ? runsInRange : [];
  const sorted = [...list].sort((a, b) => String(a?.run_date).localeCompare(String(b?.run_date)));
  if (!Number.isFinite(maxRuns) || maxRuns <= 0 || sorted.length <= maxRuns) {
    return { runs: sorted, dropped: 0 };
  }
  return { runs: sorted.slice(sorted.length - maxRuns), dropped: sorted.length - maxRuns };
}
